import { getOptimizedImageUrl } from "@/components/_helpers";
import { createBeatTracker } from "@/utils/arcade/beat";
import type { ArcadeGameKind } from "@/utils/arcade/games";
import { createRng, type ArcadeTheme } from "@/utils/arcade/theme";
import { context2d, createArtKit, createPostFx, defaultCanvasFactory } from "./engine/render";
import { createGame, prepareGame } from "./engine";

// Card thumbnails are real frames of the game: the engine plays itself on the
// song's theme and cover for a few simulated seconds, then one frame goes
// through the same bloom pass as the live stage. Nothing is a stock image.

export interface ThumbnailRequest {
  kind: ArcadeGameKind;
  theme: ArcadeTheme;
  coverUrl?: string;
  width: number;
  height: number;
  seconds?: number;
}

const SIM_FPS = 30;
// A job waits in one queue. `wanted` counts the cards still asking for it, so
// a card that unmounts or changes theme (the cover tint lands a moment after
// the first render) drops its place instead of painting a frame nobody sees.
interface ThumbnailJob {
  promise: Promise<string | null>;
  wanted: number;
}
const cache = new Map<string, ThumbnailJob>();
const covers = new Map<string, Promise<HTMLImageElement | null>>();
let queue: Promise<unknown> = Promise.resolve();

const keyFor = (request: ThumbnailRequest) =>
  [request.kind, request.theme.seed, request.theme.palette.primary, request.coverUrl ?? "", request.width, request.height].join("|");

function loadCover(url: string | undefined): Promise<HTMLImageElement | null> {
  if (!url || typeof Image === "undefined") return Promise.resolve(null);
  const existing = covers.get(url);
  if (existing) return existing;
  const promise = new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = getOptimizedImageUrl(url, { width: 320, height: 320, fit: "cover", quality: 80 });
  });
  covers.set(url, promise);
  return promise;
}

const idle = () =>
  new Promise<void>((resolve) => {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number }).requestIdleCallback;
    // A running game keeps the main thread busy, so idle time rarely comes.
    // A paint takes about 10ms, so a short wait is enough to stay polite.
    if (ric) ric(() => resolve(), { timeout: 120 });
    else setTimeout(resolve, 16);
  });

export async function paintGameFrame(request: ThumbnailRequest, cover: CanvasImageSource | null, scale = 2) {
  const pixelWidth = Math.round(request.width * scale);
  const pixelHeight = Math.round(request.height * scale);
  const canvas = defaultCanvasFactory(pixelWidth, pixelHeight);
  const ctx = context2d(canvas);
  if (!canvas || !ctx) return null;
  const art = createArtKit(() => cover);
  const engine = createGame(request.kind, {
    width: request.width,
    height: request.height,
    theme: request.theme,
    art,
    reducedMotion: false,
    difficulty: 1,
    keyHints: false,
    quiet: true,
    rng: createRng(request.theme.seed ^ 0x5bd1e995),
  });
  const tracker = createBeatTracker();
  const frames = Math.round((request.seconds ?? 6.5) * SIM_FPS);
  let audio = tracker.update(null, 0, request.theme.bpm, null);
  for (let i = 0; i < frames; i++) {
    audio = tracker.update(null, (i * 1000) / SIM_FPS, request.theme.bpm, null);
    engine.update(1 / SIM_FPS, engine.autopilot(audio), audio);
  }
  // Land the picture right on a beat so it shows the game at full swing.
  audio = { ...audio, pulse: 0.85 };
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  engine.draw(ctx, audio);
  createPostFx().apply(ctx, canvas as CanvasImageSource, pixelWidth, pixelHeight, audio, engine.bloom);
  engine.dispose?.();
  return canvas;
}

async function toUrl(canvas: HTMLCanvasElement | OffscreenCanvas): Promise<string | null> {
  try {
    if ("convertToBlob" in canvas) {
      const blob = await canvas.convertToBlob({ type: "image/webp", quality: 0.86 });
      return URL.createObjectURL(blob);
    }
    return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : null), "image/webp", 0.86));
  } catch {
    return null;
  }
}

export function renderGameThumbnail(request: ThumbnailRequest, signal?: AbortSignal): Promise<string | null> {
  const key = keyFor(request);
  let entry = cache.get(key);
  if (!entry) {
    const job: ThumbnailJob = { promise: Promise.resolve(null), wanted: 0 };
    job.promise = queue.then(async () => {
      if (job.wanted <= 0) {
        cache.delete(key);
        return null;
      }
      const [cover] = await Promise.all([loadCover(request.coverUrl), prepareGame(request.kind)]);
      await idle();
      const canvas = await paintGameFrame(request, cover);
      return canvas ? await toUrl(canvas) : null;
    });
    queue = job.promise.catch(() => null);
    cache.set(key, job);
    entry = job;
  }
  const job = entry;
  job.wanted += 1;
  signal?.addEventListener("abort", () => {
    job.wanted -= 1;
  }, { once: true });
  return job.promise;
}
